import { 
  Box, 
  Button, 
  Icon, 
  Select, 
  VStack,
  HStack,
  Text,
  Badge,
} from '@chakra-ui/react';
import { PiCardsBold } from 'react-icons/pi';
import { FaBuilding, FaExclamationTriangle, FaUserAlt, FaAddressCard, FaSitemap, FaBroadcastTower } from "react-icons/fa";
import { BiSolidBellRing } from "react-icons/bi";
import { IoWatch } from 'react-icons/io5';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { getCenter } from '../../../../redux/reducers/SelectCenterReducer';
import { useEffect } from 'react';
import LanguageSwitcher from '../../../LanguageSwitcher';
import { useTranslation } from 'react-i18next';
import { useUnreadEvents } from '../../../../context/UnreadEventsContext';

interface NavButtonProps {
  label: string;
  icon: any;
  isActive: boolean;
  hasAlert?: boolean;
  alertCount?: number;
  onClick: () => void;
}

const NavButton = ({
  label,
  icon,
  isActive,
  hasAlert,
  alertCount,
  onClick,
}: NavButtonProps) => (
  <Button
    variant="ghost"
    onClick={onClick}
    w="full"
    px={3}
    py={2.5}
    h="40px"
    borderRadius="xl"
    display="flex"
    alignItems="center"
    justifyContent="space-between"
    bg={isActive ? 'rgba(13, 148, 136, 0.2)' : 'transparent'}
    color={isActive ? 'white' : 'gray.300'}
    fontWeight={isActive ? 'semibold' : 'normal'}
    borderLeft={isActive ? '3px solid' : '3px solid transparent'}
    borderLeftColor={isActive ? 'teal.400' : 'transparent'}
    _hover={{
      bg: isActive ? 'rgba(13, 148, 136, 0.28)' : 'whiteAlpha.100',
      color: 'white',
      transform: 'translateX(2px)',
    }}
    transition="all 0.15s ease"
  >
    <HStack spacing={3} overflow="hidden">
      <Icon as={icon} fontSize="18px" color={isActive ? 'teal.300' : 'gray.400'} flexShrink={0} />
      <Text fontSize="13px" isTruncated textAlign="left">
        {label}
      </Text>
    </HStack>
    {hasAlert && (
      <Badge
        colorScheme="red"
        borderRadius="full"
        px={1.5}
        py={0.2}
        fontSize="10px"
        animation="pulse 2s infinite"
      >
        {alertCount || '!'}
      </Badge>
    )}
  </Button>
);

export const NavItemSup = ({ onClose }: { onClose?: () => void }) => {
  const { user } = useSelector((state: any) => state.auth || {});
  const { selectCenter } = useSelector((state: any) => state.selectedCenter || {});
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const isSuperAdmin = Boolean(user?.isAdmin === true);
  const isCorporation = user?.role === 'corporacion';
  const isCenterAdmin = user?.role === 'administrador' && !user?.isAdmin;

  // Eventos no leídos desde el contexto global
  const { hasUnread, allEvents = [] } = useUnreadEvents();
  const unreadCount = (allEvents || []).filter((ev: any) => !ev.read).length;

  const handleNavigate = (path: string) => {
    navigate(path);
    onClose?.();
  };

  const onChangeSelectCenter = (e: any) => {
    const value = String(e.target.value ?? '');
    if (!value) return;
    const uid = user?.userId ?? user?.id;
    (dispatch as any)(getCenter({ userId: uid, centerId: value } as any));
    navigate('/home');
  };

  useEffect(() => {
    const uid = user?.userId ?? user?.id;
    if (!isSuperAdmin && Array.isArray(user?.centers) && user.centers.length === 1) {
      const singleCenter = user.centers[0];
      const targetCenterId = String(singleCenter?.centerId ?? singleCenter?.id ?? '');
      if (targetCenterId && selectCenter?.centerId !== targetCenterId) {
        (dispatch as any)(
          getCenter({
            userId: uid,
            centerId: targetCenterId,
          } as any),
        );
      }
    }
  }, [user, isSuperAdmin, selectCenter?.centerId, dispatch]);

  const pathname = location.pathname;

  return (
    <VStack w="full" spacing={2} align="stretch">
      {/* Selector de centro (si tiene más de 1 asignado) */}
      {!isSuperAdmin && Array.isArray(user?.centers) && user.centers.length > 1 && (
        <Box w="full" px={1} mb={1}>
          <Select
            value={String(selectCenter?.centerId ?? '')}
            placeholder={t('Select center') || 'Seleccionar centro'}
            onChange={onChangeSelectCenter}
            bg="whiteAlpha.100"
            borderColor="whiteAlpha.200"
            color="white"
            size="sm"
            borderRadius="lg"
            fontSize="12px"
            _hover={{ borderColor: 'teal.400' }}
            _focus={{ borderColor: 'teal.400' }}
          >
            {user.centers.map((center: any) => (
              <option
                key={String(center.centerId ?? center.id)}
                value={String(center.centerId ?? center.id ?? '')}
                style={{ backgroundColor: '#0f172a', color: 'white' }}
              >
                {center.name}
              </option>
            ))}
          </Select>
        </Box>
      )}

      {/* Selector de idioma */}
      <Box mb={2}>
        <LanguageSwitcher />
      </Box>

      {/* Enlaces de navegación */}
      <NavButton
        label={t('Home') || 'Inicio / Dashboard'}
        icon={PiCardsBold}
        isActive={pathname === '/home'}
        hasAlert={hasUnread && !isSuperAdmin && !isCorporation}
        alertCount={unreadCount}
        onClick={() => handleNavigate('/home')}
      />

      {isSuperAdmin && (
        <NavButton
          label={t('Corporations') || 'Corporaciones'}
          icon={FaSitemap}
          isActive={pathname.startsWith('/corporations')}
          onClick={() => handleNavigate('/corporations')}
        />
      )}

      {isSuperAdmin && (
        <NavButton
          label={t('Coverage Map') || 'Mapa de Cobertura'}
          icon={FaBroadcastTower}
          isActive={pathname.startsWith('/coverage-map')}
          onClick={() => handleNavigate('/coverage-map')}
        />
      )}

      {!isSuperAdmin && (isCorporation || isCenterAdmin || selectCenter?.role === 'admin') && (
        <NavButton
          label={t('Centers') || 'Centros'}
          icon={FaBuilding}
          isActive={pathname.startsWith('/centers')}
          onClick={() => handleNavigate('/centers')}
        />
      )}

      {(isSuperAdmin || isCorporation || isCenterAdmin || selectCenter) && (
        <>
          {(isSuperAdmin || isCorporation || isCenterAdmin || selectCenter?.role === 'admin') && (
            <NavButton
              label={t('Users') || 'Usuarios'}
              icon={FaUserAlt}
              isActive={pathname.startsWith('/users')}
              onClick={() => handleNavigate('/users')}
            />
          )}

          {!isSuperAdmin && !isCorporation && (isCenterAdmin || selectCenter) && (
            <NavButton
              label={t('Events') || 'Eventos & Alertas'}
              icon={BiSolidBellRing}
              isActive={pathname.startsWith('/events')}
              hasAlert={hasUnread}
              alertCount={unreadCount}
              onClick={() => handleNavigate('/events')}
            />
          )}

          {!isSuperAdmin && (isCorporation || selectCenter) && (
            <NavButton
              label={t('Patients') || 'Pacientes'}
              icon={FaAddressCard}
              isActive={pathname.startsWith('/patients')}
              onClick={() => handleNavigate('/patients')}
            />
          )}

          {(isSuperAdmin || isCorporation || isCenterAdmin || selectCenter?.role === 'admin') && (
            <NavButton
              label={t('Devices') || 'Dispositivos'}
              icon={IoWatch}
              isActive={pathname.startsWith('/devices')}
              onClick={() => handleNavigate('/devices')}
            />
          )}
        </>
      )}
    </VStack>
  );
};
